import { useAction, useAtom } from '@reatom/npm-react'
import { useEffect, useEffectEvent } from 'react'
import { Controller, useForm } from 'react-hook-form'
import styled from 'styled-components'
import { useMemo } from 'use-memo-one'

import { AsyncStateN, Redacted } from '@blade/std-lib'
import { isPasswordValid } from '@root/entities/auth'
import { useTranslation } from '@root/shared/localization'
import {
  AuthButton,
  AuthModal,
  AuthModalHeading,
} from '@root/shared/ui/auth-modal'
import {
  InputPassword,
  KeyIcon,
  ProfileIcon,
  Separator,
} from '@root/shared/ui-kit'

import { reatomInitializeSecureStorage } from '../atoms'

type FormValues = {
  password: string
  repeatPassword: string
}

type Props = {
  onInitialized?: (args: { password: Redacted.Redacted }) => void
}

export const InitializeSecureStorageForm = ({ onInitialized }: Props) => {
  const { t } = useTranslation()
  // eslint-disable-next-line @reatom/unit-naming-rule
  const model = useMemo(() => reatomInitializeSecureStorage(), [])
  const [state] = useAtom(model)

  const initialize = useAction(model.execute)

  const { control, formState, handleSubmit, getValues } = useForm<FormValues>({
    defaultValues: {
      password: '',
      repeatPassword: '',
    },
  })

  const handleSubmitInternal = handleSubmit(async (data) => {
    initialize({ password: Redacted.make(data.password) })
  })

  const handleInitialized = useEffectEvent(() => {
    onInitialized?.({ password: Redacted.make(getValues().password) })
  })

  useEffect(() => {
    if (AsyncStateN.isSuccess(state)) {
      handleInitialized()
    }
  }, [state])

  return (
    <AuthModal infoIcon={ProfileIcon} infoMessage={t('auth.info.wallets')}>
      <Form onSubmit={handleSubmitInternal}>
        <AuthModalHeading
          icon={KeyIcon}
          title={t('auth.create-local-password')}
          mobileInfoMessage={t('auth.info.wallets')}
        />
        <Separator />
        <Controller
          rules={{ required: true, validate: isPasswordValid }}
          control={control}
          name="password"
          render={({ field }) => (
            <InputPassword
              {...field}
              autoFocus
              startContent={t('auth.password')}
              autoCorrect="off"
              spellCheck="false"
              autoComplete="password"
              size="m"
            />
          )}
        />
        <Controller
          rules={{
            required: true,
            validate: (rpt, { password }) => {
              if (rpt !== password) {
                return t('settings.password.toast-repeat-error')
              }
            },
          }}
          control={control}
          name="repeatPassword"
          render={({ field }) => (
            <InputPassword
              {...field}
              startContent={t('auth.repeat')}
              autoCorrect="off"
              spellCheck="false"
              autoComplete="new-password"
              size="m"
            />
          )}
        />
        <AuthButton disabled={!formState.isValid} busy={state.loading}>
          {t('auth.continue')}
        </AuthButton>
      </Form>
    </AuthModal>
  )
}

const Form = styled.form`
  display: grid;
  gap: 16px;
`
